import React, { useState } from 'react';
import './XenditCheckout.scss';
import PurchaseService from '@/Data/PurchaseService';

interface XenditCheckoutProps {
    paymentRequestId: string;
    orderid: string;
    productid: string;
    orderName: string;
    amount: number;
    currency: string;
    channelCode: string;
    status: string;
    customerEmail?: string;
    customerName: string;
    successUrl: string;
    failUrl: string;
    clientKey: string;
    checkoutUrl?: string;
    qrCode?: string;
    qrImage?: string;
    virtualAccountNumber?: string;
    isTestMode?: boolean;
    onCancel: () => void;
}

const XenditCheckout: React.FC<XenditCheckoutProps> = ({
    paymentRequestId,
    orderid,
    productid,
    orderName,
    amount,
    currency,
    channelCode,
    checkoutUrl,
    qrCode,
    qrImage,
    virtualAccountNumber,
    isTestMode = false,
    onCancel
}) => {
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(false);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);

    const formatAmount = () => {
        const formatter = new Intl.NumberFormat(currency === 'IDR' ? 'id-ID' : 'en-US', {
            style: 'currency',
            currency: currency || 'IDR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        });
        return formatter.format(amount);
    };

    const getPaymentMethodName = () => {
        const methodNames: Record<string, string> = {
            'QRIS': 'QRIS (QR Payment)',
            'DANA': 'DANA E-Wallet',
            'OVO': 'OVO E-Wallet',
            'LINKAJA': 'LinkAja E-Wallet',
            'SHOPEEPAY': 'ShopeePay E-Wallet',
            'GCASH': 'GCash',
            'MAYA': 'Maya',
            'BRI_VIRTUAL_ACCOUNT': 'BRI Virtual Account',
            'BNI_VIRTUAL_ACCOUNT': 'BNI Virtual Account',
            'MANDIRI_VIRTUAL_ACCOUNT': 'Mandiri Virtual Account',
            'PERMATA_VIRTUAL_ACCOUNT': 'Permata Virtual Account'
        };
        return methodNames[channelCode] || channelCode;
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(err => {
            console.error('Failed to copy:', err);
        });
    };

    const handleCheckStatus = async () => {
        setCheckingStatus(true);
        setError(null);
        setStatusMessage(null);
        
        try {
            // Get success URL from props or construct it
            const successUrl = `${window.location.origin}/payment/success?orderid=${orderid}&productid=${productid}`;
            
            // Validate the Xendit payment
            const response = await PurchaseService.validateXenditPayment(
                orderid,
                productid,
                paymentRequestId || '',
                amount
            );
            
            if (response?.data?.valid) {
                // Payment successful - redirect to success page
                setStatusMessage('Payment confirmed! Redirecting...');
                setTimeout(() => {
                    window.location.href = successUrl;
                }, 1500);
            } else {
                // Payment not yet completed
                setStatusMessage('Payment not yet completed. Please complete the payment and try again.');
                setCheckingStatus(false);
            }
        } catch (error: any) {
            console.error('Error checking payment status:', error);
            
            // Check if payment is still pending
            if (error?.response?.data?.message?.includes('PENDING') || 
                error?.response?.data?.message?.includes('not yet completed')) {
                setStatusMessage('Payment is still pending. Please complete the payment and try again.');
            } else {
                setError('Failed to check payment status. Please try again.');
            }
            setCheckingStatus(false);
        }
    };

    const handleProceedToEwallet = () => {
        if (checkoutUrl) {
            window.open(checkoutUrl, '_blank');
        }
    };

    if (error) {
        return (
            <div className="xendit-checkout-container">
                <div className="xendit-error">
                    <h3>{"Payment Error"}</h3>
                    <p>{error}</p>
                    <button onClick={() => setError(null)} className="cancel-button">
                        {"Go Back"}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="xendit-checkout-container">
            <div className="xendit-payment-info">
                <h3>{"Payment Details"}</h3>
                <div className="payment-summary">
                    <div className="summary-row">
                        <span className="label">{"Product:"}</span>
                        <span className="value">{orderName}</span>
                    </div>
                    <div className="summary-row">
                        <span className="label">{"Amount:"}</span>
                        <span className="value amount">{formatAmount()}</span>
                    </div>
                    <div className="summary-row">
                        <span className="label">{"Payment Method:"}</span>
                        <span className="value">{getPaymentMethodName()}</span>
                    </div>
                    {isTestMode && (
                        <p className="test-mode-notice">
                            {"(Test Mode - No actual payment will be processed)"}
                        </p>
                    )}
                </div>
            </div>

            {/* QRIS Payment */}
            {channelCode === 'QRIS' && (qrImage || qrCode) && (
                <div className="xendit-qris-payment">
                    <h4>{"Scan QR Code to Pay"}</h4>
                    <div className="qr-code-container">
                        <img 
                            src={qrImage || `https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(qrCode!)}`} 
                            alt="Payment QR Code" 
                            className="qr-code-image" 
                            onError={() => {
                                console.error('Failed to load QR code image');
                                setError('Failed to generate QR code. Please try again.');
                            }}
                        />
                    </div>
                    <div className="qr-instructions">
                        <p>{"Scan this QR code with any of these apps:"}</p>
                        <ul>
                            <li>{"OVO"}</li>
                            <li>{"DANA"}</li>
                            <li>{"GoPay"}</li>
                            <li>{"LinkAja"}</li>
                            <li>{"ShopeePay"}</li>
                            <li>{"Any QRIS-compatible banking app"}</li>
                        </ul>
                    </div>
                    <div className="qr-expiry-notice">
                        <p>{"⏱️ This QR code will expire in 15 minutes"}</p>
                    </div>
                </div>
            )}

            {/* Virtual Account Payment */}
            {virtualAccountNumber && (
                <div className="xendit-va-payment">
                    <h4>{"Bank Transfer Details"}</h4>
                    <div className="va-container">
                        <div className="va-bank-name">{getPaymentMethodName()}</div>
                        <div className="va-number-container">
                            <div className="va-number">{virtualAccountNumber}</div>
                            <button 
                                className="copy-button"
                                onClick={() => copyToClipboard(virtualAccountNumber)}
                            >
                                {copied ? "✓ Copied" : "Copy"}
                            </button>
                        </div>
                        <div className="va-amount">
                            <span>{"Transfer Amount: "}</span>
                            <strong>{formatAmount()}</strong>
                        </div>
                    </div>
                    <div className="va-instructions">
                        <h5>{"How to Pay:"}</h5>
                        <ol>
                            <li>{"Open your mobile banking or ATM"}</li>
                            <li>{"Select Transfer to Virtual Account"}</li>
                            <li>{"Enter the virtual account number above"}</li>
                            <li>{"Confirm the amount matches exactly"}</li>
                            <li>{"Complete the transfer"}</li>
                        </ol>
                    </div>
                    <div className="va-expiry-notice">
                        <p>{"⏱️ This virtual account will expire in 24 hours"}</p>
                    </div>
                </div>
            )}

            {/* E-Wallet Redirect Payment */}
            {checkoutUrl && !qrCode && !virtualAccountNumber && (
                <div className="xendit-ewallet-payment">
                    <h4>{"E-Wallet Payment"}</h4>
                    <div className="ewallet-container">
                        <p>{"Click the button below to complete payment with "}{getPaymentMethodName()}</p>
                        <button 
                            className="ewallet-button"
                            onClick={handleProceedToEwallet}
                        >
                            {"Pay with "}{getPaymentMethodName()}
                        </button>
                        <p className="ewallet-note">
                            {"You will be redirected to "}{getPaymentMethodName()}{" to complete the payment"}
                        </p>
                    </div>
                </div>
            )}

            {/* Status Check Section */}
            <div className="xendit-status-section">
                <p className="status-text">{"After completing payment, click the button below:"}</p>
                <button 
                    className="check-status-button"
                    onClick={handleCheckStatus}
                    disabled={checkingStatus}
                >
                    {checkingStatus ? "Checking Payment..." : "I've Completed Payment"}
                </button>
                {statusMessage && (
                    <div className={`status-message ${statusMessage.includes('confirmed') ? 'success' : 'pending'}`}>
                        {statusMessage}
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            <div className="xendit-actions">
                <button 
                    className="cancel-button"
                    onClick={onCancel}
                >
                    {"Cancel Payment"}
                </button>
            </div>
        </div>
    );
};

export default XenditCheckout;